'use client'
import { RecommendedSearch } from '@/payload-types'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import { useState } from 'react'

type Language = 'en' | 'fr' | 'ar'
type LocalizedText = {
  en?: string
  fr?: string
  ar?: string
}
type Props = {
  className?: string
  dictionary: any
  lang?: Language
  categories: any[]
} & RecommendedSearch

const categoryNames: Record<string, LocalizedText> = {
  apartments: {
    en: 'Apartments',
    fr: 'Appartements',
    ar: 'شقق',
  },
  villas: {
    en: 'Villas',
    fr: 'Villas',
    ar: 'فيلات',
  },
  offices: {
    en: 'Offices',
    fr: 'Bureaux',
    ar: 'مكاتب',
  },
  lands: {
    en: 'Lands',
    fr: 'Terrains',
    ar: 'أراضي',
  },
}

export const RecommendedSearchBlock = ({
  className,
  dictionary,
  lang = 'en',
  categories = [],
  initialVisibleItems = 5,
}: Props) => {
  const [expandedCategories, setExpandedCategories] = useState<{
    [key: number]: boolean
  }>({})

  const toggleCategory = (index: number) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }
  const getCategoryTitle = (value: any): string => {
    return categoryNames[value]?.[lang] || value
  }

  const getSearchUrl = (search: any): string => {
    return search.searchLink || '#'
  }

  if (!categories || categories.length === 0) return null

  return (
    <div className={cn('pt-5 bg-light', className)}>
      <div className="container mx-auto max-sm:px-4">
        {/* Main Heading */}
        <div className="flex items-center mb-5">
          <h2 className="lg:text-h2 md:text-h3 text-h4 font-semibold text-typography">
            {dictionary?.recommendedSearches?.heading || 'Popular real estate searches'}
          </h2>
        </div>
        {/* Categories Grid */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4">
          {categories.map((item: { category: string; searches: any[] }, index: number) => {
            const categoryIndex = index
            const isExpanded = expandedCategories[categoryIndex]
            const visibleSearches = isExpanded ? item.searches : item.searches.slice(0, (initialVisibleItems || 5))
            const hasMore = item.searches?.length > (initialVisibleItems || 5)
            return (
              <div key={index}>
                <h4 className="md:text-xl text-md text-gray-600 font-semibold capitalize md:mb-4 mb-2">
                  {getCategoryTitle(item.category)}
                </h4>
                {/* Search Links */}
                <ul className="md:text-sm text-xs text-[#00994C] flex flex-col gap-3 py-3">
                  {visibleSearches?.map(
                    (search: { searchTitle: string; searchLink?: string }, index: number) => (
                      <li className="hover:underline cursor-pointer" key={index}>
                        <Link href={getSearchUrl(search)}>{search.searchTitle}</Link>
                      </li>
                    ),
                  )}
                  {/* View More/Less Button */}
                  {hasMore && (
                    <li className="mt-2">
                      <button
                        onClick={() => toggleCategory(categoryIndex)}
                        className="text-sm text-gray-700 font-semibold hover:underline focus:outline-none flex items-center gap-1"
                        type="button"
                      >
                        {isExpanded
                          ? dictionary?.propertyListing?.viewLessText || 'Voir moins'
                          : dictionary?.propertyListing?.viewMoreText || 'Voir plus'}
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )
          })}
        </div>
        <hr className="border border-gray-200 mt-6" />
      </div>
    </div>
  )
}
